import { FC } from 'react';
import Head from 'next/head';
import Image from "next/legacy/image";
import StoreCard from 'components/StoreCard/StoreCard';
import { STORE_LOCATIONS } from 'data/store-locations';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import StoreBg from '../../../public/images/stores.webp';
import ListingSeoText from 'components/Others/ListingSeoText';
import LazyHydrate from 'react-lazy-hydration';
import { getLocaleUrl } from 'functions/getLocaleUrl';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import { SeoMetaTags } from 'constants/dataTypes';
import { getSeoMetaTags } from 'utils/common.utils';
import { useRouter } from 'next/router';

const getUserCountry = (locale: string | undefined) => {
  switch (locale) {
    case 'ca':
      return 5; // Canada
    case 'au':
      return 6; // Australia
    case 'nz':
      return 7; // New Zealand
    default:
      return null;
  }
};

interface StoreLocationViewProps {
  stateStores: any;
  seoMetaTags: SeoMetaTags;
  stores: any;
}

export default function StoreLocationView({
  stateStores,
  seoMetaTags,
  stores
}: StoreLocationViewProps) {
  const router = useRouter();
  const userCountryId = getUserCountry(router.locale);

  const breadcrumbData = [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: 'stores',
      text: 'Stores'
    },
    {
      id: 3,
      href: '',
      text: stateStores.state.name
    }
  ];
  const seo = STORE_LOCATIONS.find(
    (location: any) => location.path === stateStores.state.slug
  )?.seo;
  const h1Text = seo?.h1;
  const description = seo?.description;
  const title = seo?.title;

  const storeLink = (url: string) => {
    return (
      (router.locale !== 'international' ? '/' + router.locale : '') +
      url
    );
  }

  // First, group stores by region_id (country) and state/city
  const groupedStores = stores.reduce((acc: any, store: any) => {
    // Skip stores that are not active or should be hidden
    if (!store.is_active || store.hide_in_ecommerce) {
      return acc;
    }

    const countryId = store.region_id;
    const countryMapping: Record<number, string> = {
      6: 'Australia',
      5: 'Canada',
      7: 'New Zealand',
    };
    const countryName = countryMapping[countryId] || 'Other';

    // Use city for Canada, state for other countries
    const locationGroup = countryId === 5 ? store.city : store.state;

    // Initialize country if doesn't exist
    if (!acc[countryId]) {
      acc[countryId] = {
        country: { id: countryId, name: countryName },
        states: {}
      };
    }

    // Initialize state/city if doesn't exist
    if (!acc[countryId].states[locationGroup]) {
      acc[countryId].states[locationGroup] = {
        state: {
          name: locationGroup || 'Unknown Location',
          slug: locationGroup ? locationGroup.toLowerCase().replace(/\s+/g, '-') : 'unknown-location'
        },
        stores: []
      };
    }

    // Add store to state/city
    acc[countryId].states[locationGroup].stores.push(store);

    return acc;
  }, {});
  // Add these configuration constants at the top of the file
  const COUNTRY_ORDER = [
    'Australia',
    'Canada',
    'New Zealand'
  ];

  const STATE_ORDER: any = {
    'Australia': ['NSW', 'VIC', 'QLD'],
    'Canada': ['Toronto', 'Hamilton'],
    'New Zealand': [] // Add NZ regions if needed
  };

  const CITY_ORDER: any = {
    'NSW': ['Newtown', 'Rozelle'],
    'VIC': ['Brunswick', 'Fitzroy', 'Geelong'],
    'QLD': ['West End'],
    'Toronto': ['Little Italy', 'Riverside'],
    'Hamilton': ['Hamilton']
  };

  // Update the sorting logic
  const formattedStores = Object.values(groupedStores)
    .map((country: any) => {
      const states = Object.values(country.states);

      // Sort states according to configured order
      states.sort((a: any, b: any) => {
        const stateOrder = STATE_ORDER[country.country.name] || [];
        const aIndex = stateOrder.indexOf(a.state.name);
        const bIndex = stateOrder.indexOf(b.state.name);
        if (aIndex === -1 && bIndex === -1) return a.state.name.localeCompare(b.state.name);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });

      // Sort stores within each state/city according to configured order
      states.forEach((stateData: any) => {
        const cityOrder = CITY_ORDER[stateData.state.name] || [];
        stateData.stores.sort((a: any, b: any) => {
          const aIndex = cityOrder.findIndex((city: any) => a.name.includes(city));
          const bIndex = cityOrder.findIndex((city: any) => b.name.includes(city));
          if (aIndex === -1 && bIndex === -1) return a.name.localeCompare(b.name);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      });

      return {
        country: country.country,
        states: states
      };
    }).sort((a: any, b: any) => {
      // First prioritize user's country
      if (a.country.id === userCountryId) return -1;
      if (b.country.id === userCountryId) return 1;

      // Then sort by configured country order
      const aIndex = COUNTRY_ORDER.indexOf(a.country.name);
      const bIndex = COUNTRY_ORDER.indexOf(b.country.name);
      if (aIndex === -1 && bIndex === -1) return a.country.name.localeCompare(b.country.name);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  const countryData = formattedStores.find((country: any) => country.country.id === stateStores.stores[0].region_id);
  let stateName = stateStores.stores[0].state;
  if (stateStores.stores[0].region_id === 5) {
    stateName = stateStores.stores[0].city;
  }

  return (
    <div>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
        <meta name="twitter:description" content={description} />
      </Head>
      <div className="relative h-[450px] md:h-[450px] 2xl:h-[65vh] w-full">
        {/* Background Image */}
        <div className="relative w-full h-full">
          <Image
            src={StoreBg}
            alt={`stores`}
            layout="fill"
            objectFit="cover"
            priority
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50" />
        </div>
        <div className="absolute left-0 bottom-0 w-full">
          <div className="mx-auto max-w-[1224px] py-8 px-4">
            <h1 className="p-4 font-sans text-[32px] md:text-[72px] md:leading-[72px] font-bold text-white md:text-left">
              Find a<br></br> Dresden store
            </h1>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-[32px] max-w-[1224px] px-4">
        <div className="mb-8">
          <Breadcrumb data={breadcrumbData} />
        </div>


        <div className="w-full mb-12 flex flex-col items-center">
          <div className="flex items-center justify-center gap-[24px] mb-6">
            <div className="relative w-[48px] h-[48px]">
              <Image
                src={`/images/${countryData?.country.name.replace(/\s+/g, '')}Flag.png`}
                alt={`${countryData?.country.name} flag`}
                layout="fill"
                objectFit="cover"
              />
            </div>
            <h2 className="font-sans text-[24px] md:text-[32px] text-[#2A2A2A]">
              <span className="font-bold">{countryData?.country.name}</span> locations
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {countryData?.states.map((stateData: any, idx: number) => (
              <a
                key={idx}
                href={storeLink(`/stores/${stateData.state.slug}`)}
                title={`${stateData.state.name} Glasses Store`}
                className={`min-w-[66px] px-6 py-2 font-sans font-medium text-brand-black100 bg-gray-100 rounded-[6px] transition-colors hover:bg-gray-200 border border-[#E0E0E0] flex items-center justify-center ${stateName.toLowerCase() === stateData.state.name.toLowerCase() ? 'bg-[#FF8200] !text-white' : ''}`}
              >
                {stateData.state.name}
              </a>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-[24px] mt-8  w-full">
            {stateStores?.stores.map((locationStore: any) => {
              return (
                <StoreCard key={locationStore.slug} data={locationStore} />
              )
            })}
          </div>
        </div>

        <LazyHydrate whenVisible>
          <ListingSeoText />
        </LazyHydrate>
      </div>
      <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
    </div >
  );
};
type StoreLocationStaticPath = { params: { city: string }; locale?: string };
export async function getStaticPaths(options: any) {
  const res = await fetch(process.env.NEXT_PUBLIC_LARAVEL_URL + '/old-api/store/list');
  const stores = await res.json();

  // Filter active and visible stores
  const activeStores = stores.filter((store: any) =>
    store.is_active && !store.hide_in_ecommerce
  );

  const paths: StoreLocationStaticPath[] = [];

  // Group stores by state/city
  const storesByLocation = activeStores.reduce((acc: any, store: any) => {
    const locationKey = store.region_id === 5 ? store.city : store.state;
    if (!locationKey) return acc;

    if (!acc[locationKey]) {
      acc[locationKey] = {
        state: {
          name: locationKey,
          slug: locationKey.toLowerCase().replace(/\s+/g, '-')
        },
        stores: []
      };
    }
    acc[locationKey].stores.push(store);
    return acc;
  }, {});

  // Generate paths for each location and locale
  Object.values(storesByLocation).forEach((location: any) => {
    options.locales.forEach((locale: string) => {
      paths.push({
        params: { city: location.state.slug },
        locale
      });
    });
  });

  return {
    paths,
    fallback: false
  };
}

export async function getStaticProps({
  params,
  locale
}: {
  params: { city?: string };
  locale: string;
}) {
  const res = await fetch(process.env.NEXT_PUBLIC_LARAVEL_URL + '/old-api/store/list');
  const stores = await res.json();

  // Filter active and visible stores
  const activeStores = stores.filter((store: any) =>
    store.is_active && !store.hide_in_ecommerce &&
    store.name.toLowerCase() !== 'replenish'
  );

  // Group stores by state/city
  const storesByLocation = activeStores.reduce((acc: any, store: any) => {
    const locationKey = store.region_id === 5 ? store.city : store.state;
    if (!locationKey) return acc;

    const slug = locationKey.toLowerCase().replace(/\s+/g, '-');
    if (slug === params.city) {
      if (!acc[locationKey]) {
        acc[locationKey] = {
          state: {
            name: locationKey,
            slug: slug
          },
          stores: []
        };
      }
      acc[locationKey].stores.push(store);
    }
    return acc;
  }, {});

  const locationKey = Object.keys(storesByLocation)[0];
  if (!locationKey) {
    return {
      redirect: {
        destination: getLocaleUrl('/stores', locale),
        permanent: false
      }
    };
  }

  const seoMetaTags = await getSeoMetaTags('/stores/' + params.city, locale);

  return {
    props: {
      stateStores: storesByLocation[locationKey],
      seoMetaTags,
      stores: activeStores
    }
  };
}

